import type {
  StagedStoreLogoUpload,
  StoreLogoPublicUrl,
  StoreLogoStoragePath,
} from '../types/store';
import { getFileExtension, MAX_IMAGE_BYTES } from './image-validation';

export const STORE_LOGO_BUCKET = 'store-logos' as const;
export const STORE_LOGO_STORAGE_PATH_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/(?:logo|logos\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\.(jpg|png|webp)$/;

const ALLOWED_STORE_LOGO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
type StoreLogoContentType = (typeof ALLOWED_STORE_LOGO_CONTENT_TYPES)[number];

export function assertStoreLogoContentType(contentType: string): StoreLogoContentType {
  if (!ALLOWED_STORE_LOGO_CONTENT_TYPES.includes(contentType as StoreLogoContentType)) {
    throw new Error('Invalid store logo content type');
  }
  return contentType as StoreLogoContentType;
}

export function buildVersionedStoreLogoPath(
  storeId: string,
  contentType: StoreLogoContentType,
  assetId = crypto.randomUUID(),
): StoreLogoStoragePath {
  const path = `${storeId}/logos/${assetId}.${getFileExtension(contentType)}`;
  return assertStoreLogoStoragePathForStore(storeId, path);
}

export function parseStoreLogoPathFromPublicUrl(
  publicUrl: string,
  expectedSupabaseUrl: string,
): StoreLogoStoragePath | null {
  const marker = `/storage/v1/object/public/${STORE_LOGO_BUCKET}/`;
  let url: URL;
  try {
    url = new URL(publicUrl);
  } catch {
    return null;
  }
  if (url.origin !== getOrigin(expectedSupabaseUrl)) return null;
  const markerIndex = url.pathname.indexOf(marker);
  if (markerIndex === -1) return null;
  const encodedPath = url.pathname.slice(markerIndex + marker.length);
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(encodedPath);
  } catch {
    return null;
  }
  if (!STORE_LOGO_STORAGE_PATH_RE.test(decodedPath)) return null;
  return decodedPath as StoreLogoStoragePath;
}

export function assertStoreLogoPublicUrlForStore(
  storeId: string,
  publicUrl: string,
  expectedSupabaseUrl: string,
): StoreLogoPublicUrl {
  const path = parseStoreLogoPathFromPublicUrl(publicUrl, expectedSupabaseUrl);
  if (!path) throw new Error('Invalid store logo public URL');
  assertStoreLogoStoragePathForStore(storeId, path);
  return publicUrl as StoreLogoPublicUrl;
}

export function assertStagedStoreLogoForStore(
  storeId: string,
  staged: StagedStoreLogoUpload,
  expectedSupabaseUrl: string,
): StagedStoreLogoUpload {
  if (staged.bucket !== STORE_LOGO_BUCKET) throw new Error('Invalid store logo bucket');
  assertStoreLogoStoragePathForStore(storeId, staged.path);
  assertStoreLogoPublicUrlForStore(storeId, staged.publicUrl, expectedSupabaseUrl);
  assertStoreLogoContentType(staged.contentType);
  if (staged.sizeBytes > MAX_IMAGE_BYTES) throw new Error('Store logo is too large');
  return staged;
}

export function getSupabaseUrlFromUnknown(client: unknown): string {
  if (!client || typeof client !== 'object') {
    throw new Error('Supabase client URL is required for store logo validation');
  }
  const candidate = (client as Record<string, unknown>).supabaseUrl;
  if (typeof candidate !== 'string' || candidate.length === 0) {
    throw new Error('Supabase client URL is required for store logo validation');
  }
  getOrigin(candidate);
  return candidate;
}

function getOrigin(rawUrl: string): string {
  try {
    return new URL(rawUrl).origin;
  } catch {
    throw new Error('Invalid Supabase URL for store logo validation');
  }
}

function assertStoreLogoStoragePathForStore(storeId: string, path: string): StoreLogoStoragePath {
  if (path.includes('..') || path.startsWith('/') || path.includes('\\')) {
    throw new Error('Invalid store logo path');
  }
  if (!STORE_LOGO_STORAGE_PATH_RE.test(path)) throw new Error('Invalid store logo path');
  if (path.split('/')[0] !== storeId) throw new Error('Store logo belongs to another store');
  return path as StoreLogoStoragePath;
}
